import React, { useEffect, useRef, useState } from 'react';
import {
  Box, HStack, Spinner, Text, useToast,
} from '@chakra-ui/react';
import axios from 'axios';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Window {
  on: boolean;
  message?: string | null;
  untilUtc?: string | null;
}

// pure: is this axios error the API's maintenance refusal? (item 1576)
export const isMaintenanceRefusal = (err: any): boolean => {
  const res = err?.response;
  if (!res || res.status !== 503) return false;
  const h = res.headers ?? {};
  return h['x-basix-maintenance'] === '1' || String(res.data ?? '').startsWith('Basix is updating');
};

// item 1576 (Chad: "an option to stop Basix use with a front end toast stating maintenance while
// the deploy is going") — while a deploy holds the maintenance window open, every open tab shows
// a pinned banner and the API refuses saves (503). A refused save toasts loudly so nobody thinks
// it went through. When the window closes, "back" toasts; NewVersionWatcher then offers the
// refresh onto the new build. Polls every 30s (10s while on) — the GET is anonymous and cheap.
const MaintenanceBanner: React.FC = () => {
  const [win, setWin] = useState<Window>({ on: false });
  const wasOn = useRef(false);
  const toast = useToast();

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const r = await axios.get(`${apiUrl()}maintenance`);
        if (!live) return;
        const next = r.data as Window;
        if (wasOn.current && !next.on) {
          toast({
            id: 'maintenance-back',
            status: 'success',
            title: 'Basix is back',
            description: 'The update is done — saving works again.',
            duration: 8000,
            isClosable: true,
          });
        }
        wasOn.current = !!next.on;
        setWin(next);
      } catch {
        // the API restarting mid-deploy is expected — keep the last state, try again
      }
      if (live) timer = setTimeout(tick, wasOn.current ? 10_000 : 30_000);
    };
    tick();
    return () => { live = false; clearTimeout(timer); };
  }, [toast]);

  // a save the API refused during maintenance must never look like it worked
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (err) => {
      if (isMaintenanceRefusal(err)) {
        wasOn.current = true;
        setWin((w) => ({ ...w, on: true }));
        if (!toast.isActive('maintenance-refused')) {
          toast({
            id: 'maintenance-refused',
            status: 'warning',
            title: 'Not saved — Basix is updating',
            description: 'Your change was not saved. Keep this page open and try again in a few minutes.',
            duration: 10000,
            isClosable: true,
          });
        }
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, [toast]);

  if (!win.on) return null;
  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={2000}
      bg="orange.400"
      color="black"
      px={4}
      py={2}
      boxShadow="md"
      role="status"
    >
      <HStack justify="center" spacing={3}>
        <Spinner size="sm" />
        <Text fontWeight="semibold" fontSize="sm">
          {win.message || 'Basix is updating. Saving is paused for a few minutes.'}
        </Text>
        <Text fontSize="xs" display={{ base: 'none', md: 'block' }}>
          You can keep reading — anything you save now won&apos;t go through.
        </Text>
      </HStack>
    </Box>
  );
};

export default MaintenanceBanner;
